// src/components/team/TeamSummaryCard.jsx
import React from "react";
import { Card, Icon } from "@blueprintjs/core";
import { isoToRo } from "../../utils/helpers";

/** ex: "2003-08-13" + nume → "13 aug – Nume (21 ani)" */
import { ro } from "date-fns/locale";
import { format, parseISO, differenceInYears, isValid } from "date-fns";

function formatBirthdayOnThisYear(birth_date, name) {
    const dob = parseISO(birth_date);
    const y = new Date().getFullYear();
    const month = Number(String(birth_date).slice(5, 7)) - 1; // 0-based
    const day = Number(String(birth_date).slice(8, 10));
    let anniv = new Date(y, month, day);
    if (!isValid(anniv)) anniv = new Date(y, 1, 28);
    const age = differenceInYears(anniv, dob);
    return `${format(anniv, "d MMM", { locale: ro })} – ${name} (${age} ani)`;
}

// mic utilitar pt. sărbătoriți (stilul "azi" / "urmează")
const dayOf = (iso) => Number(String(iso).slice(8, 10));

export default function TeamSummaryCard({
    team,
    accentColor = "#2da3ff",
    onClickTitle,           // funcție la click pe titlu (ex: deschide dashboard-ul echipei)
    onDelete,               // opțional (doar în AdminEchipe)
    showActions = true,     // dacă arăți icoana de ștergere etc.
}) {
    if (!team) return null;

    const birthdays = team?.birthdays || [];
    const todayDay = new Date().getDate();
    const hasToday = birthdays.some(p => dayOf(p.birth_date) === todayDay);
    const nextIndex = hasToday ? -1 : birthdays.findIndex(p => dayOf(p.birth_date) > todayDay);

    return (
        <Card className="org-card card-l1 dashboard-bg" style={{ borderLeft: `5px solid ${accentColor}` }}>
            <div className="rel-pos card-header">
                <div className="org-link" onClick={onClickTitle}>
                    {team.name}
                </div>

                <div className="card-text-1">
                    <img src="/icons/img_avatar.png" alt="Avatar" />
                    <div>Antrenor: {team.coach?.name || ""}</div>
                </div>

                {showActions && (
                    <Icon icon="trash" onClick={onDelete} />
                )}
            </div>

            <div className="card-content">
                <div className="info-columns">
                    {/* Jucători */}
                    <div className="info-block">
                        <div className="info-title font-darkcyan">
                            Jucatori {team?.playerCount}
                        </div>

                        <div className="info-line">
                            <div className="info-line-label">
                                Apti: <span>{team?.playerCount - (team?.injuredPlayers?.length || 0) - (team?.suspendedPlayers?.length || 0)}</span>
                            </div>
                        </div>

                        <div className="info-line">
                            <div className="info-line-label">
                                {/* <Icon icon="blocked-person" size='14' /> */}
                                Suspendati: <span>{team?.suspendedPlayers?.length || 0}</span>
                                {team?.suspendedPlayers?.length
                                    ? (
                                        <ol>
                                            {team.suspendedPlayers.map(p => <li key={p.id}>{p.name}</li>)}
                                        </ol>
                                    ) : null}



                            </div>
                        </div>

                        <div className="info-line">
                            <div className="info-line-label">
                                 Accidentați: <span>{team?.injuredPlayers?.length || 0}</span>
                                {team?.injuredPlayers?.length
                                    ? (
                                        <ol>
                                            {team.injuredPlayers.map(p => <li key={p.id}>{p.name} <Icon icon='symbol-cross' size='14' /></li>)}
                                        </ol>
                                    ) : null}
                            </div>
                        </div>
                    </div>

                    {/* Jocuri */}
                    <div className="info-block">
                        <div className="info-title font-darkcyan">
                            Jocuri <img className="xs-img" src="/icons/cup.png" />
                        </div>

                        <div className="info-line">
                            <div className="info-line-label">
                                Ultimul joc: <span>{isoToRo(team?.lastGame?.date)}</span>
                            </div>

                            {team?.lastGame && (
                                <div className="game-info-row">
                                    <div className={`order-${team?.lastGame?.home_away === "acasa" ? "1" : "3"}`}>{team.name}</div>
                                    <div className="order-2"> - </div>
                                    <div className={`order-${team?.lastGame?.home_away === "acasa" ? "3" : "1"}`}>{team?.lastGame?.opponent}</div>

                                    <div className={`m-right-1 order-${team?.lastGame?.home_away === "acasa" ? "4" : "6"}`}>{team?.lastGame?.team_score}</div>
                                    <div className="order-5"> - </div>
                                    <div className={`order-${team?.lastGame?.home_away === "acasa" ? "6" : "4"}`}>{team?.lastGame?.opponent_score}</div>
                                </div>
                            )}
                        </div>

                        <div className="info-line">
                            <div className="info-line-label">
                                Urmatorul joc: <span>{isoToRo(team?.nextGame?.date)}</span>
                            </div>

                            {team?.nextGame && (
                                <div className="game-info-row">
                                    <div className={`order-${team?.nextGame?.home_away === "acasa" ? "1" : "3"}`}>{team.name}</div>
                                    <div className="order-2"> - </div>
                                    <div className={`order-${team?.nextGame?.home_away === "acasa" ? "3" : "1"}`}>{team?.nextGame?.opponent}</div>
                                </div>
                            )}
                        </div>

                        <div className="info-line">
                            <div className="info-line-label">Ultimele rezultate:</div>
                            {team?.last5Results
                                .filter(Boolean)
                                .map((r, idx) => (
                                    <span key={idx} className={`result result-${r.toLowerCase()} order-${idx + 1}`}>{r}</span>
                                ))}
                        </div>
                    </div>

                    {/* Antrenamente */}
                    <div className="info-block">
                        <div className="info-title font-darkcyan">
                            Antrenamente <img className="xs-img" src="/icons/training.png" />
                        </div>

                        <div className="info-line">
                            <div className="info-line-label">Ultimul antrenament:</div>
                            <span className="font-2">
                                {isoToRo(team?.lastTraining?.date)} {team?.lastTraining?.location && `(Teren: ${team?.lastTraining?.location})`}
                            </span>
                        </div>

                        <div className="info-line">
                            <div className="info-line-label">Următorul antrenament:</div>
                            <span className="font-2">
                                {isoToRo(team?.nextTraining?.date)} {team?.nextTraining?.location && `(Teren: ${team?.nextTraining?.location})`}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Goluri & Assist */}
                <div className="info-two-columns">
                    <div className="info-block">
                        <div className="info-title font-darkcyan">
                            Actiuni de gol ({team?.topScorers?.reduce((sum, p) => sum + p.goals, 0) || 0} goluri)
                        </div>

                        <div className="flex-three-cols">
                            <div className="info-line">
                                <div className="info-subtitle">Total: Goluri + Assist-uri</div>
                                <ul className="scorers-list">
                                    {(team?.topScorers?.length || team?.topAssists?.length)
                                        ? (
                                            Object.values(
                                                [
                                                    ...(team.topScorers || []),
                                                    ...(team.topAssists || []),
                                                ].reduce((acc, p) => {
                                                    if (!acc[p.id]) {
                                                        acc[p.id] = { id: p.id, name: p.name, goals: 0, assists: 0 };
                                                    }
                                                    if (p.goals) acc[p.id].goals = p.goals;
                                                    if (p.assists) acc[p.id].assists = p.assists;
                                                    acc[p.id].total = (acc[p.id].goals || 0) + (acc[p.id].assists || 0);
                                                    return acc;
                                                }, {})
                                            )
                                                .sort((a, b) => b.total - a.total)
                                                .map((p, k) => (
                                                    <li key={p.id || `${p.name}-${k}`}>
                                                        <span className="scorer-name">{k + 1}.&nbsp;{p.name}</span>
                                                        <span className="scorer-goals">{p.total}</span>
                                                    </li>
                                                ))
                                        )
                                        : <li className="empty-info">Nu există contribuții în interval</li>
                                    }
                                </ul>
                            </div>

                            <div className="info-line">
                                <div className="info-subtitle">Goluri</div>
                                <ul className="scorers-list">
                                    {team?.topScorers?.length
                                        ? team.topScorers.map((p, k) => (
                                            <li key={p.id || `${p.name}-${k}`}>
                                                <span className="scorer-name">{k + 1}.&nbsp;{p.name}</span>
                                                <span className="scorer-goals">{p.goals}&nbsp;{p.goals === 1 ? "gol" : "goluri"}</span>
                                            </li>
                                        ))
                                        : <li className="empty-info">Nu există goluri în interval</li>
                                    }
                                </ul>
                            </div>

                            <div className="info-line">
                                <div className="info-subtitle">Pase decisive</div>
                                <ul className="scorers-list">
                                    {team?.topAssists?.length
                                        ? team.topAssists.map((p, k) => (
                                            <li key={p.id || `${p.name}-${k}`}>
                                                <span className="scorer-name">{k + 1}.&nbsp;{p.name}</span>
                                                <span className="scorer-goals">{p.assists}</span>
                                            </li>
                                        ))
                                        : <li className="empty-info">Nu există assist-uri în interval</li>
                                    }
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Cartonașe */}
                <div className="info-two-columns">
                    <div className="info-block">
                        <div className="info-title font-darkcyan">
                            Cartonase <img className="xs-img" src="/icons/y-r.png" />
                        </div>

                        <div className="flex-two-cols">
                            <div className="info-line">
                                <div className="info-subtitle">Cartonașe galbene <img className="xs-img" src="/icons/yellowcard.png" /></div>
                                <ul className="scorers-list">
                                    {team?.yellowCards?.length
                                        ? team.yellowCards.map((p, k) => (
                                            <li key={p.id || `${p.name}-${k}`}>
                                                <span className="scorer-name">{k + 1}.&nbsp;{p.name}</span>
                                                <span className="scorer-goals">{p.yellows}</span>
                                            </li>
                                        ))
                                        : <li className="empty-info">Nu sunt cartonașe galbene</li>
                                    }
                                </ul>
                            </div>

                            <div className="info-line">
                                <div className="info-subtitle">Cartonașe roșii <img className="xs-img" src="/icons/redcard.png" /></div>
                                <ul className="scorers-list">
                                    {team?.redCards?.length
                                        ? team.redCards.map((p, k) => (
                                            <li key={p.id || `${p.name}-${k}`}>
                                                <span className="scorer-name">{k + 1}.&nbsp;{p.name}</span>
                                                <span className="scorer-goals">{p.reds}</span>
                                            </li>
                                        ))
                                        : <li className="empty-info">Nu sunt cartonașe roșii</li>
                                    }
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Sărbătoriții lunii */}
                <div className="info-two-columns">
                    <div className="info-block">
                        <div className="info-title font-darkcyan">Info</div>
                        <div className="flex-two-cols">
                            <div className="info-line">
                                <div className="info-subtitle">Sărbătoriții lunii</div>
                                <ul className="scorers-list">
                                    {birthdays.length
                                        ? birthdays.map((p, k) => {
                                            const d = dayOf(p.birth_date);
                                            const classMod = d === todayDay ? "scorer--today" : (!hasToday && k === nextIndex ? "scorer--next" : "");
                                            return (
                                                <li key={p.id} className={classMod}>
                                                    <span className="scorer-name">{formatBirthdayOnThisYear(p.birth_date, p.name)}</span>
                                                </li>
                                            );
                                        })
                                        : <li className="empty-info">Nu sunt sărbătoriți în luna curentă</li>
                                    }
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </Card>
    );
}
